/**
 * Created by Zhao, Marcus on 10/20/2022.
 * Description: support setting fetchapp /fetchemail on tamaleweb.
 * AdminEmailSetting Component
 * ------ maintenance history ------
 */
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { catchError, filter, map, skip, switchMap, take } from 'rxjs/operators';
import { Subscription } from 'rxjs';
import { EmailSettingService } from '../../../../services/email-setting.service';
import { EmailSetting, MailSetting } from './admin-email-setting.model';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ToastService } from '../../../../widgets/toast/toast.service';
import { StringLiteralsPipe } from '../../../../pipes/translate.pipe';
import { NotificationStyles, NotificationOptions } from '../../../../widgets/notification/notification.model';
import { AlertButton, AlertBtnTypes } from '../../../../widgets/alert-window/alert-window.model';
import { AlertWindowService } from '../../../../widgets/alert-window/alert-window.service';
import { DatePipe } from '@angular/common';
import { FileHelperService } from '../../../../services/file-helper.service';
import { AuthService } from '../../../../tamalelibs/services/auth.service';

const exchangeOnline = StringLiteralsPipe.translate('email.exchange_online');
const start = StringLiteralsPipe.translate('email.start');
const stop = StringLiteralsPipe.translate('email.stop');
const restart = StringLiteralsPipe.translate('email.restart');
const status = StringLiteralsPipe.translate('email.status');
const dateFormat = 'MM/dd/yyyy HH:mm:ss';
@Component({
    selector: 'tam-admin-email-setting',
    templateUrl: './admin-email-setting.component.html',
    styleUrls: ['./admin-email-setting.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class AdminEmailSettingComponent implements OnInit, OnDestroy {
    //#region public properties
    emailProperties: EmailSetting;
    mailProperties: MailSetting;

    emailSettingForm = new UntypedFormGroup({
        emailAddress: new UntypedFormControl(''),
        folderName: new UntypedFormControl(''),
        keepOnServer: new UntypedFormControl(''),
        receivedDatetime: new UntypedFormControl(),
        takeOverlapDays: new UntypedFormControl(''),
        periodSeconds: new UntypedFormControl(''),
        emailServer: new UntypedFormControl({ value: exchangeOnline, disabled: true })
    });

    mailSettingForm = new UntypedFormGroup({
        mailAddressFrom: new UntypedFormControl(''),
        useSMTPAuth: new UntypedFormControl(false),
        SMTPAuthUser: new UntypedFormControl(''),
        SMTPAuthPassword: new UntypedFormControl(''),
        SMTPEncryptionSecurity: new UntypedFormControl(''),
        mailPersonalName: new UntypedFormControl(''),
        emailSMTPServerAddress: new UntypedFormControl(''),
        emailSMTPServerPortAddress: new UntypedFormControl(''),
        relayToAddress: new UntypedFormControl(''),
        fromDisplayName: new UntypedFormControl(''),
    });

    fetchappStatusButton = StringLiteralsPipe.translate('email.start_fetchapp');
    fetchappProperties;

    isDisabledFetchMailButton = true;
    isStartFetchApp = false;
    isLoadingStop = false;
    isLoadingStart = false;
    isLoadingRestart = false;
    isLoadingExport = false;
    isLoadingStopEmail = false;
    isLoadingSubmit = false;
    isLoadingSubmit2 = false;
    isChanged = false;
    isChanged2 = false;
    isShowActionButton = false;

    keepEmailServerData = [true, false];
    keepSmtpServeSercurity = ['NONE', 'SSL/TLS', 'STARTTLS'];
    keepReplyAdress = ['Mailer From Address', 'Submitter\'s Email Address'];
    keepDisplayName = ['Mailer Personal Name', 'Submitter\'s Personal Name'];

    get emailAddress() {
        return this.emailSettingForm.get('emailAddress');
    }

    get emailServer() {
        return this.emailSettingForm.get('emailServer');
    }

    get folderName() {
        return this.emailSettingForm.get('folderName');
    }

    get keepOnServer() {
        return this.emailSettingForm.get('keepOnServer');
    }

    get receivedDatetime() {
        return this.emailSettingForm.get('receivedDatetime');
    }

    get takeOverlapDays() {
        return this.emailSettingForm.get('takeOverlapDays');
    }

    get periodSeconds() {
        return this.emailSettingForm.get('periodSeconds');
    }

    get mailAddressFrom() {
        return this.mailSettingForm.get('mailAddressFrom');
    }

    get useSMTPAuth() {
        return this.mailSettingForm.get('useSMTPAuth');
    }

    get SMTPAuthUser() {
        return this.mailSettingForm.get('SMTPAuthUser');
    }

    get SMTPAuthPassword() {
        return this.mailSettingForm.get('SMTPAuthPassword');
    }
    get SMTPEncryptionSecurity() {
        return this.mailSettingForm.get('SMTPEncryptionSecurity');
    }
    get mailPersonalName() {
        return this.mailSettingForm.get('mailPersonalName');
    }
    get emailSMTPServerAddress() {
        return this.mailSettingForm.get('emailSMTPServerAddress');
    }
    get emailSMTPServerPortAddress() {
        return this.mailSettingForm.get('emailSMTPServerPortAddress');
    }
    get relayToAddress() {
        return this.mailSettingForm.get('relayToAddress');
    }
    get fromDisplayName() {
        return this.mailSettingForm.get('fromDisplayName');
    }

    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];
    private _emailReg = /^(\w-*\.*)+@(\w-?)+(\.\w{2,})+$/;


    //#endregion

    constructor(
        private _alertWindow: AlertWindowService,
        private _authService: AuthService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _emailSettingService: EmailSettingService,
        private _fileHelperService: FileHelperService,
        private _datePipe: DatePipe,
        private _toastService: ToastService) { }

    //#region public methods

    ngOnInit() {
        this._getFetchEmailSettings();
        this._putFetchEmailAction(status);
        this._putFetachAppAction(status);
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    /**
     * Stop fetch  mail.
     */
    clickStopFetchMail() {
        this.isLoadingStopEmail = true;
        this._putFetchEmailAction(stop);
    }

    /**
     *  Click submit button and send to server.
     */
    clickSubmitSetting() {
        this.isLoadingSubmit = true;
        if (this._doValidation()) {
            this.emailProperties.emailAddress = this.emailAddress.value;
            this.emailProperties.folderName = this.folderName.value;
            this.emailProperties.receviedTimeStamp = this.receivedDatetime.value.getTime().toString();
            this.emailProperties.keepOnServer = this.keepOnServer.value;
            this.emailProperties.takeOverlapDays = this.takeOverlapDays.value;
            this.emailProperties.timer = EmailSetting.setTime(this.periodSeconds.value);
            this._setEmailSettingProperties();
        } else {
            this.isLoadingSubmit = false;
        }
    }

    clickSubmitMailSetting() {
        this.isLoadingSubmit2 = true;
        if (this._doValidation2()) {
            this.mailProperties.mailAddressFrom = this.mailAddressFrom.value;
            this.mailProperties.useSMTPAuth = this.useSMTPAuth.value;
            this.mailProperties.SMTPAuthUser = this.SMTPAuthUser.value;
            this.mailProperties.SMTPAuthPassword = this._authService.encryptPassword(this.SMTPAuthPassword.value);
            this.mailProperties.SMTPEncryptionSecurity = this.SMTPEncryptionSecurity.value;
            this.mailProperties.mailPersonalName = this.mailPersonalName.value;
            this.mailProperties.emailSMTPServerAddress = this.emailSMTPServerAddress.value;
            this.mailProperties.emailSMTPServerPortAddress = this.emailSMTPServerPortAddress.value;
            this.mailProperties.relayToAddress = this.relayToAddress.value;
            this.mailProperties.fromDisplayName = this.fromDisplayName.value;
            this._setMailSettingProperties();
        } else {
            this.isLoadingSubmit2 = false;
        }
    }

    /*
     *  verify contorls' status if the control's value is changed
    */
    configChanged() {
        this.isChanged = true;
        this._doValidation();
    }


    clickExportLog() {
        this.isLoadingExport = true;
        this._getEmailSettingConfig();
    }

    clickKeepSeverChanged(event) {
        this.isChanged = true;
    }

    clickRestartFetchapp() {
        this._showAlertForFetchAppAction(restart);

    }

    clickStopFetchApp() {
        this._showAlertForFetchAppAction(stop);
    }

    clickStartFetchApp() {
        this._showAlertForFetchAppAction(start);
    }

    clickMailChanged() {
        this.isChanged2 = true;
        this._doValidation2();
    }

    changeAnthStatus(useSMTPAuth: boolean) {
        this.useSMTPAuth.setValue(!this.useSMTPAuth.value);
        this._setValuesByAuthStatus();
        this.clickMailChanged();
    }

    //#endregion


    //#region private methods

    /**
     * validation for form control.
     * @returns dataValid
     */
    private _doValidation(): boolean {
        let dataValid = true;
        if (!this.emailServer.value) {
            this.emailServer.setErrors({
                required: true
            });
            dataValid = false;
        }

        if (!this._emailReg.test(this.emailAddress.value)) {
            this.emailAddress.setErrors({
                'invalidEmail': true
            });
            dataValid = false;
        }

        if (!this.emailAddress.value) {
            this.emailAddress.setErrors({
                required: true
            });
            dataValid = false;
        }

        if (!this.folderName.value) {
            this.folderName.setErrors({
                required: true
            });
            dataValid = false;
        }

        if (!this.periodSeconds.value) {
            this.periodSeconds.setErrors({
                required: true
            });
            dataValid = false;
        }

        if (!this.takeOverlapDays.value) {
            this.takeOverlapDays.setErrors({
                required: true
            });
            dataValid = false;
        }
        if (!this.receivedDatetime.value) {
            this.receivedDatetime.setErrors({
                required: true
            });
            dataValid = false;
        }
        return dataValid;
    }

    /**
     * validation2 for form control.
     * @returns dataValid2
     */
    private _doValidation2(): boolean {
        let dataValid = true;
        if (!this.mailAddressFrom.value) {
            this.mailAddressFrom.setErrors({
                required: true
            });
            dataValid = false;
        }

        if (!this.SMTPAuthUser.value) {
            this.SMTPAuthUser.setErrors({
                required: true
            });
            dataValid = false;
            if (!this.useSMTPAuth.value) {
                dataValid = true;
            }
        }

        if (!this.SMTPAuthPassword.value) {
            this.SMTPAuthPassword.setErrors({
                required: true
            });
            dataValid = false;
            if (!this.useSMTPAuth.value) {
                dataValid = true;
            }
        }
        if (!this.SMTPEncryptionSecurity.value) {
            this.SMTPEncryptionSecurity.setErrors({
                required: true
            });
            dataValid = false;
        }
        if (!this.mailPersonalName.value) {
            this.mailPersonalName.setErrors({
                required: true
            });
            dataValid = false;
        }
        if (!this.emailSMTPServerAddress.value) {
            this.emailSMTPServerAddress.setErrors({
                required: true
            });
            dataValid = false;
        }
        if (!this.emailSMTPServerPortAddress.value) {
            this.emailSMTPServerPortAddress.setErrors({
                required: true
            });
            dataValid = false;
        }
        if (!this.relayToAddress.value) {
            this.relayToAddress.setErrors({
                required: true
            });
            dataValid = false;
        }
        if (!this.fromDisplayName.value) {
            this.fromDisplayName.setErrors({
                required: true
            });
            dataValid = false;
        }
        return dataValid;
    }


    /**
     * Init email setting data from server.
     * @param data
     */
    private _initEmailSettingData(data: EmailSetting) {
        this.emailAddress.setValue(data.emailAddress);
        this.folderName.setValue(data.folderName);
        this.keepOnServer.setValue(data.keepOnServer);
        if (data.receviedTimeStamp) {
            const date = this._datePipe.transform(data.receviedTimeStamp, dateFormat);
            this.receivedDatetime.setValue(new Date(date));
        }
        this.takeOverlapDays.setValue(data.takeOverlapDays);
        this.periodSeconds.setValue(EmailSetting.parseTime(data.timer));
    }

    private _initMailSettingData(data: MailSetting) {
        this.mailAddressFrom.setValue(data.mailAddressFrom);
        this.useSMTPAuth.setValue(data.useSMTPAuth);
        this.SMTPAuthUser.setValue(data.SMTPAuthUser);
        this.SMTPAuthPassword.setValue(this._authService.decryptPassword(data.SMTPAuthPassword));
        this.SMTPEncryptionSecurity.setValue(data.SMTPEncryptionSecurity);
        this.mailPersonalName.setValue(data.mailPersonalName);
        this.emailSMTPServerAddress.setValue(data.emailSMTPServerAddress);
        this.emailSMTPServerPortAddress.setValue(data.emailSMTPServerPortAddress);
        this.relayToAddress.setValue(data.relayToAddress);
        this.fromDisplayName.setValue(data.fromDisplayName);
    }

    /**
     * Download email setting config from sever.
     */
    private _getEmailSettingConfig() {
        this._emailSettingService.getEmailSettingConfig().pipe(take(1)).subscribe(data => {
            this._fileHelperService.downloadFunction(data, 'FetchAppConfig.zip');
            this.isLoadingExport = false;
            this._changeDetectorRef.detectChanges();
        }, error => {
            this.isLoadingExport = false;
            this._changeDetectorRef.detectChanges();
        });
    }

    /**
     *  Get fetch email&&mail properties.
     */
    private _getFetchEmailSettings() {
        this._emailSettingService.getEmailSettingProperties().pipe(take(1)).subscribe(data => {
            this.emailProperties = EmailSetting.parse(data.fetchapp);
            this.mailProperties = MailSetting.parse(data.outbound);
            this._initEmailSettingData(this.emailProperties);
            this._initMailSettingData(this.mailProperties);
            this._setValuesByAuthStatus();
        }, error => {
            this._toast(NotificationStyles.Error, error.message);
        });
    }

    /**
     * Get /set fetch email status.
     *
     * @param action(status,start,stop)
     */
    private _putFetchEmailAction(action: string) {
        this._destroySubscriptions.push(
            this._emailSettingService.putFetchEmailAction(action).subscribe(res => {
                this.isLoadingStopEmail = false;
                if (res.message === 'started') {
                    this.isDisabledFetchMailButton = false;
                } else {
                    this.isDisabledFetchMailButton = true;
                }
                if (action !== status) {
                    this._toast(NotificationStyles.Success, StringLiteralsPipe.translate('email.stop_fetchemail_successfully'));
                }

                if (action === stop) {
                    // Due to server need to use utc as date for fetch app server.
                    const date = this._datePipe.transform(res.date, dateFormat);
                    this.receivedDatetime.setValue(new Date(date));
                }
                this._changeDetectorRef.detectChanges();
            },
                error => {
                    this.isLoadingStopEmail = false;
                    this._changeDetectorRef.detectChanges();
                    this._toast(NotificationStyles.Error, error.message);
                })
        );
    }

    /**
     * Get /set fetch app status.
     *
     * @param action(status,start,stop)
     */
    private _putFetachAppAction(action: string) {
        this._emailSettingService.putEmailAction(action).subscribe(res => {
            this.isShowActionButton = true;
            this.isLoadingStop = false;
            this.isLoadingStart = false;
            this.isLoadingRestart = false;
            if (((action === start || action === restart) && res.message === 'successfully') || res.message === 'started') {
                this.isStartFetchApp = true;
                this.fetchappStatusButton = StringLiteralsPipe.translate('email.stop_fetchapp');
            } else {
                this.isStartFetchApp = false;
                this.fetchappStatusButton = StringLiteralsPipe.translate('email.start_fetchapp');
            }
            if (action !== status) {
                let tip = '';
                if (action === start) {
                    tip = 'Start';
                } else if (action === stop) {
                    tip = 'Stop';
                } else if (action === restart) {
                    tip = 'Restart';
                }
                this._toast(NotificationStyles.Success, `${tip} fetchapp ${res.message}`);
            }
            this._changeDetectorRef.detectChanges();

        }, error => {
            this.isLoadingStop = false;
            this.isLoadingStart = false;
            this.isLoadingRestart = false;
            this._toast(NotificationStyles.Error, error.message);
        });
    }

    private _showAlertForFetchAppAction(action: string) {
        if (action === status) {
            return;
        }
        const cancelBtn: AlertButton = {
            text: 'Cancel',
            type: AlertBtnTypes.tertiary
        };
        const confirmButton: AlertButton = {
            text: 'Confirm',
            type: AlertBtnTypes.primary
        };

        const subscription = this._alertWindow.
            custom('Confirmation required', [`Please confirm your operation on ${action}ing Fetchapp.`], cancelBtn, confirmButton)
            .subscribe((result: boolean) => {
                subscription.unsubscribe();
                if (result) {
                    if (action === start) {
                        this.isLoadingStart = true;
                    } else if (action === restart) {
                        this.isLoadingRestart = true;
                    } else if (action === stop) {
                        this.isLoadingStop = true;
                    }
                    this._changeDetectorRef.detectChanges();
                    this._putFetachAppAction(action);
                }
            });
    }


    /**
     * Submit email properties.
     */
    private _setEmailSettingProperties() {
        this._emailSettingService.setEmailSettingProperties(this.emailProperties).pipe(take(1)).subscribe(data => {
            this.isLoadingSubmit = false;
            this.isChanged = false;
            this._changeDetectorRef.detectChanges();
            if (data) {
                this._toast(NotificationStyles.Success, StringLiteralsPipe.translate('email.all_settings_are_submitted_successfully'));
            }
        }, error => {
            this.isLoadingSubmit = false;
            this._changeDetectorRef.detectChanges();
            this._toast(NotificationStyles.Error, error.message);
        });
    }

    /**
     * Submit Mail properties.
     */
    private _setMailSettingProperties() {
        this._emailSettingService.setOutboundEmailSettings(this.mailProperties).pipe(take(1)).subscribe(data => {
            this.isLoadingSubmit2 = false;
            this.isChanged2 = false;
            this._changeDetectorRef.detectChanges();
            if (data) {
                this._toast(NotificationStyles.Success, StringLiteralsPipe.translate('email.all_settings_are_submitted_successfully'));
            }
        }, error => {
            this.isLoadingSubmit2 = false;
            this._changeDetectorRef.detectChanges();
            this._toast(NotificationStyles.Error, error.message);
        });
    }

    // Control permissions based on the "Enable SMTP authentication" status
    private _setValuesByAuthStatus(): void {
        // Control whether authUser and authPassword are in read-only mode
        if (!this.useSMTPAuth.value) {
            // Disable authUser and authPassword if SMTP is enabled
            this.SMTPAuthUser.disable();
            this.SMTPAuthPassword.disable();
        } else {
            // Enable authUser and authPassword if SMTP is disabled
            this.SMTPAuthUser.enable();
            this.SMTPAuthPassword.enable();
        }
    }



    /**
     * unify toast notification.
     * @param type
     * @param msg
     */
    private _toast(type: NotificationStyles, msg: string) {
        const note = new NotificationOptions();
        note.style = type;
        note.message = msg;
        this._toastService.notify(note);
        this._changeDetectorRef.detectChanges();
    }

    //#endregion
}
